"use client"

import { useRef, useState, useEffect } from "react"

interface ScrollRevealProps {
    children: React.ReactNode
    /** Stagger delay in ms (default 0) */
    delay?: number
    className?: string
}

/**
 * Scroll-triggered reveal wrapper using IntersectionObserver.
 * Fades + slides children up when they enter the viewport.
 * Respects prefers-reduced-motion.
 */
export default function ScrollReveal({ children, delay = 0, className = "" }: ScrollRevealProps) {
    const ref = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setIsVisible(true)
            return
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    observer.unobserve(el)
                }
            },
            { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 650ms cubic-bezier(0.25, 1, 0.5, 1) ${delay}ms, transform 650ms cubic-bezier(0.25, 1, 0.5, 1) ${delay}ms`,
            }}
        >
            {children}
        </div>
    )
}
